import { type FC, forwardRef, type HTMLAttributes, type PropsWithChildren, useMemo } from 'react';

import { Link } from '@vakantiesnl/components/src/atoms/Link';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import Markdown from 'markdown-to-jsx';

const LinkWrapper: FC<PropsWithChildren<{ href: string }>> = ({ children, href, ...props }) => (
	<Link href={href} {...props}>
		{children}
	</Link>
);

const H1Markdown: FC<PropsWithChildren> = ({ children, ...props }) => (
	<Typography variant="heading2Xl" as="h1" {...props}>
		{children}
	</Typography>
);

const H2Markdown: FC<PropsWithChildren> = ({ children, ...props }) => (
	<Typography variant="headingLg" as="h2" {...props}>
		{children}
	</Typography>
);
const H3Markdown: FC<PropsWithChildren> = ({ children, ...props }) => (
	<Typography variant="headingMd" as="h3" {...props}>
		{children}
	</Typography>
);

const H4Markdown: FC<PropsWithChildren> = ({ children, ...props }) => (
	<Typography variant="headingXs" as="h4" {...props}>
		{children}
	</Typography>
);
const ParagraphMarkdown: FC<PropsWithChildren> = ({ children, ...props }) => (
	<Typography variant="bodyMd" as="p" {...props}>
		{children}
	</Typography>
);

interface Props extends HTMLAttributes<HTMLDivElement> {
	content: string;
	makeTextShortOnMobile?: boolean;
}

export const MarkdownText = forwardRef<HTMLDivElement, Props>(({ content, ...rest }, ref) => {
	const options = useMemo(() => {
		return {
			forceBlock: true,
			overrides: {
				h1: H1Markdown,
				h2: H2Markdown,
				h3: H3Markdown,
				h4: H4Markdown,
				p: ParagraphMarkdown,
				a: LinkWrapper,
			},
		};
	}, []);

	return (
		<div data-qa="markdown-text" ref={ref} {...rest}>
			<Markdown options={options}>{content.replace(/\n+/g, '<br/>')}</Markdown>
		</div>
	);
});

MarkdownText.displayName = 'MarkdownText';
